import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Spinner, Alert, Card, Modal, Form, Button, Carousel } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ItemCard, { Item } from '../components/ItemCard';
import Navbar from '../components/Navbar';
import { API_BASE_URL, ENDPOINTS, DEFAULT_STORE_ID } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

interface StoreItem {
  storeItemId: number;
  itemId?: number;
  itemName?: string;
  itemNameEn?: string;
  itemCode?: string;
  price?: number;
  stockQuantity: number;
  minStockLevel?: number;
  maxStockLevel?: number;
  isAvailable: boolean;
  isFeatured: boolean;
  currencyName?: string;
  images?: Array<{
    imageUrl?: string;
    imagePath?: string;
    isDefault?: boolean;
  }>;
  description?: string;
  categoryId?: number;
  categoryName?: string;
  minimumOrderQuantity?: number;
  leadTimeDays?: number;
  videoUrl?: string;
  isExternalOrder?: boolean;
  pricingTiers?: Array<{
    storeItemPricingTierId: number;
    storeItemId: number;
    minQuantity: number;
    maxQuantity: number | null;
    unitPrice: number;
    isActive: boolean;
    displayOrder: number;
  }>;
  shippingDetails?: Array<{
    storeShippingMethodId: number;
    methodNameAr: string;
    methodNameEn: string;
    price: number;
    minDays: number;
    maxDays: number;
  }>;
}

interface Category {
  categoryId: number;
  categoryName: string;
  categoryNameEn?: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
  itemsCount: number;
}

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = parseInt(searchParams.get('storeId') || DEFAULT_STORE_ID.toString());
  const { user, isAuthenticated } = useAuth();
  
  const [items, setItems] = useState<Item[]>([]);
  const [featuredItems, setFeaturedItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortOption, setSortOption] = useState<string>('newest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [storeName, setStoreName] = useState<string>('');

  const getCategoryName = (category: Category | undefined) => {
    if (!category) return '';
    return i18n.language === 'en' && category.categoryNameEn ? category.categoryNameEn : category.categoryName;
  };
  
  const featuredItems2 = [
    {
      id: 1,
      name: t('factoryToDoor'),
      image: "https://micgreen.com/img/sudlinetruck.png", // صورة شحن لوجستي
      price: t('fullService'),
      Website: t('SudlineWebsite'),
      stockStatus: "featured"
    },
    {
      id: 2,
      name: t('productionLines'),
      image: "https://micgreen.com/img/sudlineemp.png", // صورة خط إنتاج
      price: t('onDemand'),
      stockStatus: "featured"
    },
    {
      id: 3,
      name: t('chinaSuppliers'),
      image: "https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=1935&auto=format&fit=crop", // صورة مستودعات/بضائع
      price: t('bestPrice'),
      stockStatus: "limitedStock"
    }
  ];

  // Product Request Modal State
  const [showProductRequestModal, setShowProductRequestModal] = useState(false);
  const [productRequestForm, setProductRequestForm] = useState({
    productName: '',
    specifications: '',
    quantity: '',
    notes: '',
    requestType: '1'
  });
  const [productRequestLoading, setProductRequestLoading] = useState(false);
  const [productRequestError, setProductRequestError] = useState<string | null>(null);
  const [productRequestSuccess, setProductRequestSuccess] = useState(false);

  const handleCategoryClick = (categoryId: number | null) => {
    // إذا كان التصنيف مفعلاً بالفعل، قم بإلغاء التفعيل (إظهار جميع المنتجات)
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
    } else {
      setSelectedCategoryId(categoryId);
    }
  };



  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CATEGORIES}?isActive=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchStoreItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
     
      // Fetch store info
      const storeResponse = await fetch(`${API_BASE_URL}${ENDPOINTS.STORES}/${storeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (storeResponse.ok) {
        const store = await storeResponse.json();
        setStoreName(store.storeName || '');
      }

      // Build query string
      let queryParams = 'isAvailable=true';
      if (selectedCategoryId) {
        queryParams += `&categoryId=${selectedCategoryId}`;
      }
      // Fetch store items
      const itemsResponse = await fetch(`${API_BASE_URL}${ENDPOINTS.STORE_ITEMS(storeId)}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!itemsResponse.ok) {
        throw new Error(t('fetchItemsFailed') as string);
      }

      const storeItems: StoreItem[] = await itemsResponse.json();
      
      // Transform StoreItem to Item format with pricing tiers
      const transformedItems: Item[] = storeItems.map((storeItem) => {
        // Determine stock status
        let stockStatus: 'inStock' | 'limitedStock' | 'outOfStock' = 'inStock';
        if (storeItem.stockQuantity <= 0) {
          stockStatus = 'outOfStock';
        } else if (storeItem.minStockLevel && storeItem.stockQuantity <= storeItem.minStockLevel) {
          stockStatus = 'limitedStock';
        }

        // Get image URL
        const defaultImage = storeItem.images?.find(img => img.isDefault) || storeItem.images?.[0];
        const imageUrl = defaultImage?.imageUrl || defaultImage?.imagePath || 'https://micgreen.com/img/LogoT.png';

        // Use pricing tiers from API response (already included)
        let itemPrice = storeItem.price || 0;

        const pricingTiers = storeItem.pricingTiers || [];
        if (pricingTiers.length > 0) {
          // Sort tiers by displayOrder and minQuantity, then use the first one (index 0) as default price
          const sortedTiers = [...pricingTiers].sort(
            (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.minQuantity - b.minQuantity
          );
          itemPrice = sortedTiers[0].unitPrice;
        }

        return {
          id: storeItem.storeItemId,
          name: storeItem.itemName || 'غير محدد',
          nameEn: storeItem.itemNameEn,
          price: itemPrice,
          currencyName: storeItem.currencyName,
          image: imageUrl,
          description: storeItem.description || '',
          category: storeItem.categoryName || 'general',
          categoryId: storeItem.categoryId,
          categoryName: storeItem.categoryName,
          stockStatus: storeItem.isAvailable ? stockStatus : 'outOfStock',
          deliveryDays: storeItem.leadTimeDays || 10,
          paymentOption: 'partial' as const,
          minimumOrderQuantity: storeItem.minimumOrderQuantity || 1,
          videoUrl: storeItem.videoUrl,
          isExternalOrder: storeItem.isExternalOrder || false,
          images: storeItem.images || [],
          pricingTiers: pricingTiers.length > 0 ? pricingTiers : undefined,
          shippingDetails: storeItem.shippingDetails
        };
      });

      setItems(transformedItems);
      setFeaturedItems(transformedItems.filter(item => {
        const storeItem = storeItems.find(si => si.storeItemId === item.id);
        return storeItem?.isFeatured;
      }));
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء جلب المنتجات');
      console.error('Error fetching store items:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId, selectedCategoryId, t]);

  useEffect(() => {
    fetchCategories();
  }, [storeId]);

  useEffect(() => {
    fetchStoreItems();
  }, [fetchStoreItems]);

  // Filter items based on search term
  const filteredItems = React.useMemo(() => {
    let result = items;

    // Filter by Search Term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.categoryName?.toLowerCase().includes(searchLower)
      );
    }

    // Sort Items
    switch (sortOption) {
      case 'priceLowToHigh':
        result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'priceHighToLow':
        result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'newest':
        // Assuming higher ID means newer, or if there is a created date. Using ID for now.
        result = [...result].sort((a, b) => b.id - a.id);
        break;
      case 'bestSelling':
        // If there's no sales data, maybe stick to default or random? keeping as is for now
        break;
      default:
        break;
    }

    return result;
  }, [items, searchTerm, sortOption]);

  // Product Request Modal Handlers
  const handleProductRequestChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProductRequestForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProductRequestError(null);
    setProductRequestLoading(true);
    setProductRequestSuccess(false);

    if (!productRequestForm.productName.trim()) {
      setProductRequestError(t('enterProductName') as string);
      setProductRequestLoading(false);
      return;
    }

    if (!productRequestForm.quantity || parseFloat(productRequestForm.quantity) <= 0) {
      setProductRequestError(t('enterValidQuantity') as string);
      setProductRequestLoading(false);
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      // Save request to localStorage
      localStorage.setItem('pendingProductRequest', JSON.stringify(productRequestForm));
      setShowProductRequestModal(false);
      // Navigate to login
      navigate(`/login?redirect=/&message=${encodeURIComponent(t('loginToContinueRequest') as string)}`);
      setProductRequestLoading(false);
      return;
    }

    try {
      const requestBody = {
        userId: user.id && user.id > 0 ? user.id : null,
        customerId: null,
        productName: productRequestForm.productName.trim(),
        specifications: productRequestForm.specifications.trim() || null,
        requestedQuantity: parseFloat(productRequestForm.quantity),
        notes: productRequestForm.notes.trim() || null,
        requestType: parseInt(productRequestForm.requestType)
      };

      const token = localStorage.getItem('authToken');
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CREATE_PRODUCT_REQUEST(storeId)}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });
      const responseForErrorText = response.clone();

      if (!response.ok) {
        let errorMessage = 'فشل في إرسال الطلب';
        try {
          const errorData = await response.json();
          if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat().join(', ');
            errorMessage = `أخطاء في التحقق: ${validationErrors}`;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          const errorText = await responseForErrorText.text();
          errorMessage = errorText || `خطأ ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setProductRequestSuccess(true);
      localStorage.removeItem('pendingProductRequest');
      setProductRequestForm({
        productName: '',
        specifications: '',
        quantity: '',
        notes: '',
        requestType: '1'
      });

      setTimeout(() => {
        setShowProductRequestModal(false);
        setProductRequestSuccess(false);
      }, 3000);
    } catch (err: any) {
      setProductRequestError(err.message || t('errorSendingRequest') as string);
    } finally {
      setProductRequestLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>MIC Green Store - {storeName || t('welcome')}</title>
        <meta name="description" content="Shop the latest products at MIC Green Store. Electronics, clothing, books, and more!" />
      </Helmet>
      
      <Navbar 
        categories={categories.map(cat => ({
          ...cat,
          itemsCount: items.filter(item => item.categoryId === cat.categoryId).length
        }))}
        itemsCount={items.length}
        selectedCategoryId={selectedCategoryId}
        onCategoryClick={handleCategoryClick}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
      
      <div className="home-page-modern">
     
     {/* Multiple Vertical Scroll Carousels Section */}
     <section className="multi-carousels-section py-4" style={{ backgroundColor: '#f9fafb' }}>
  <Container>
    <Row className="g-4">
      {/* Featured Products Carousel - الجزء الأكبر (العرض البصري) */}
      <Col md={8}>


        <div className="products-carousel-wrapper shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
          <Carousel 
            fade 
            indicators={true}
            className="products-image-carousel"
            interval={4500}
          >
            {featuredItems2.slice(0, 6).map((item) => (
              <Carousel.Item key={item.id}>
                {/* <Link to={`/item/${item.id}`} className="text-decoration-none"> */}
                  <div className="carousel-slide-image-wrapper" style={{ position: 'relative', height: '520px' }}>
                    <img
                      className="d-block w-100 h-100"
                      src={item.image}
                      alt={item.name}
                      style={{ objectFit: 'cover' }}
                    />
                    {/* التدرج اللوني الجديد لإبراز النص والأناقة */}
                    <div className="carousel-slide-overlay" style={{
                      background: 'linear-gradient(to top, rgba(0,7,20,0.9) 0%, rgba(0,0,0,0) 60%)',
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      padding: '50px 40px', textAlign: 'right'
                    }}>
                      {/* <div className="badge bg-success mb-2 px-3 py-2" style={{ fontSize: '0.8rem' }}>
                        <i className="fas fa-check-circle me-1"></i> {t('sudline')}
                      </div> */}
                      <a href='http://www.sudline.sa' className="text-white fw-bold mb-2" style={{ fontSize: '2.4rem' }}>{item.Website}</a>
                     <br></br>
                      <div className="d-flex align-items-center justify-content-end gap-4 mb-5">

                        <span className="text-white fw-bold fs-4">{item.price}</span>
                     
                        {/* <span className="text-white-50">{t('idealForProduction')}</span> */}
                  
                      </div>
                      
                      <div className="d-flex justify-content-end gap-2">
                        {/* <button className="btn btn-warning btn-lg px-4 fw-bold shadow-sm">
                          عرض التفاصيل <i className="fas fa-chevron-left ms-2"></i>
                        </button> */}
                      </div>
                    </div>
                  </div>
                {/* </Link> */}
              </Carousel.Item>
            ))}
          </Carousel>
        </div>
      </Col>

      {/* Custom Product Request Card - الجزء الجانبي (تحويل العملاء) */}
      <Col md={4}>
        <div className="custom-product-request-card border-0 shadow-sm h-100" style={{ 
          borderRadius: '20px', 
          background: '#fff',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div className="p-4 text-center" style={{ background: 'linear-gradient(135deg, #1a4d2e 0%, #28a745 100%)', borderRadius: '20px 20px 0 0' }}>
            <div className="icon-box bg-white text-success rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '90px', height: '90px' }}>
              {/* <i className="fas fa-magic fs-3"></i> */}
              <img 
             src="https://micgreen.com/img/LogoT.png" 
             alt="MIC Green Logo" 
             className="h-20 md:h-28 mx-auto animated-pulse"
           />
            </div>
            <h3 className="text-white fw-bold mb-2">{t('customProductRequest')}</h3>
            <p className="text-white-50 small mb-0">{t('yourPartnerInGlobal')}</p>
          </div>
          
          <div className="bg-white p-4 rounded-3 shadow-sm">
            <div className="alert alert-light border-0 py-2 mb-4 d-flex align-items-center">
              <i className="fas fa-bolt text-warning me-2"></i>
              <span className="small fw-bold text-muted">{t('instantQuote')}</span>
            </div>

            <div className="process-timeline mb-4">
              {[
                { n: '1', t: t('step1Title'), d: t('step1Desc') },
                { n: '2', t: t('step2Title'), d: t('step2Desc') },
                { n: '3', t: t('step3Title'), d: t('step3Desc') }
              ].map((step, i) => (
                <div key={i} className="d-flex mb-3 align-items-start">
                  <div className="step-num bg-white text-success rounded-circle d-flex align-items-center justify-content-center fw-bold me-3 shadow-sm" style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                    {step.n}
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{step.t}</h6>
                    <p className="small text-muted mb-0">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <button 
              className="btn btn-success w-100 py-3 fw-bold rounded-pill shadow-sm"
              onClick={() => setShowProductRequestModal(true)}
              style={{ transition: 'all 0.3s' }}
            >
              <i className="fas fa-plus-circle me-2"></i>
              {t('startYourRequestNow')}
            </button>
          </div>
        </div>
      </Col>
    </Row>
  </Container>

  
</section>
        {/* Featured Products Section - Modern Design */}
        {featuredItems.length > 0 && (
          <section className="featured-section-modern">
            <Container>
              <div className="section-header-modern">
                <div>
                  <h2 className="section-title-modern">
                    <i className="fas fa-star me-2"></i>
                    {t('featuredProducts')}
                  </h2>
                  <p className="section-subtitle">{t('featuredProductsDesc')}</p>
                </div>
                <Link to="/?featured=true" className="view-all-btn-modern">
                  {t('viewAll')} <i className="fas fa-arrow-left ms-2"></i>
                </Link>
              </div>
              <Row xs={2} sm={3} md={4} lg={5} xl={6} className="g-4">
                {featuredItems.slice(0, 12).map((item, index) => (
                  <Col key={item.id}>
                    <div style={{ '--index': index } as React.CSSProperties}>
                      <ItemCard item={item} />
                    </div>
                  </Col>
                ))}
              </Row>
            </Container>
          </section>
        )}

        {/* All Products Section - Modern Design */}
        <section className="products-section-modern">
          <Container>
            <div className="products-content-wrapper">
              <div className="section-header-modern">
                <div>
                  <h2 className="section-title-modern">
                    <i className="fas fa-th-large me-2"></i>
                    {selectedCategoryId 
                      ? getCategoryName(categories.find(c => c.categoryId === selectedCategoryId)) || t('products')
                      : t('allProducts')
                    }
                  </h2>
                  <p className="section-subtitle">
                    {selectedCategoryId 
                      ? t('discoverCategory', { category: getCategoryName(categories.find(c => c.categoryId === selectedCategoryId)) })
                      : t('discoverProducts')
                    }
                  </p>
                </div>
                {selectedCategoryId && (
                  <button 
                    className="clear-filter-btn"
                    onClick={() => handleCategoryClick(null)}
                    title={t('showAllProducts') as string}
                  >
                    <i className="fas fa-times me-2"></i>
                    {t('clearFilter')}
                  </button>
                )}
              </div>
            
            {loading ? (
              <div className="loading-container-modern">
                <div className="loading-spinner-modern">
                  <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                </div>
                <p className="loading-text">جاري تحميل المنتجات...</p>
              </div>
            ) : error ? (
              <Alert variant="danger" className="error-alert-modern">
                <Alert.Heading>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  خطأ
                </Alert.Heading>
                <p>{error}</p>
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => fetchStoreItems()}
                >
                  <i className="fas fa-redo me-2"></i>
                  إعادة المحاولة
                </button>
              </Alert>
            ) : filteredItems.length === 0 ? (
              <Alert variant="info" className="empty-alert-modern">
                <div className="empty-icon">
                  <i className="fas fa-search"></i>
                </div>
                <Alert.Heading>لا توجد نتائج</Alert.Heading>
                <p>
                  {searchTerm 
                    ? `لم نجد منتجات تطابق "${searchTerm}"`
                    : `لا توجد منتجات متاحة حالياً ${selectedCategoryId ? 'في هذا التصنيف' : 'في هذا المتجر'}`
                  }
                </p>
                {searchTerm && (
                  <button 
                    className="btn btn-outline-primary mt-3"
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="fas fa-times me-2"></i>
                    مسح البحث
                  </button>
                )}
                {selectedCategoryId && !searchTerm && (
                  <button 
                    className="btn btn-outline-primary mt-3"
                    onClick={() => handleCategoryClick(null)}
                  >
                    عرض جميع المنتجات
                  </button>
                )}
              </Alert>
            ) : (
              <>
                <div className="products-grid-header">
                  <span className="products-count">
                    {t('showingCount', { count: filteredItems.length })}
                  </span>
                  {searchTerm && (
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times me-2"></i>
                      مسح البحث
                    </button>
                  )}
                  <div className="sort-options">
                    <select 
                      className="form-select form-select-sm sort-select"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                    >
                      <option value="newest">{t('sortByNewest')}</option>
                      <option value="priceLowToHigh">{t('sortByPriceLowToHigh')}</option>
                      <option value="priceHighToLow">{t('sortByPriceHighToLow')}</option>
                      <option value="bestSelling">{t('sortByBestSelling')}</option>
                    </select>
                  </div>
                </div>
                <Row xs={2} sm={3} md={4} lg={5} xl={6} className="g-4">
                  {filteredItems.map((item) => (
                    <Col key={item.id}>
                      <ItemCard item={item} />
                    </Col>
                  ))}
                </Row>
              </>
            )}
            </div>
          </Container>
        </section>
   {/* Hero Banner Section - Modern Design */}
   <section className="hero-banner-section">
          <Container fluid className="px-0">
            <div className="hero-banner">
              <div className="hero-content">
                <div className="hero-logo">
                  <div className="hero-logo-icon">
                  <img 
                src="https://micgreen.com/img/LogoT.png" 
                alt="MIC Green Logo" 
                className="h-20 md:h-28 mx-auto animated-pulse"
              />
                  </div>
               
                </div>
  
                <p className="hero-mission-text">
                  {t('heroMissionText')}
                </p>
                <div className="hero-stats">
                  <div className="stat-item">
                    <i className="fas fa-box-open"></i>
                    <div>
                      <strong>+{items.length}</strong>
                      <span>{t('productAvailable')}</span>
                    </div>
                  </div>
                  <div 
                    className="stat-item clickable-stat-item" 
                    onClick={() => {
                      setProductRequestForm(prev => ({
                        ...prev,
                        notes: t('sourceGulf') + (prev.notes ? '\n' + prev.notes : '')
                      }));
                      setShowProductRequestModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fas fa-shield-alt"></i>
                    <div>
                      <strong>{t('safeImport')}</strong>
                      <span>{t('sourceGulf')}</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-shipping-fast"></i>
                    <div>
                      <strong>{t('fast')}</strong>
                      <span>{t('deliveryIn30Days')}</span>
                    </div>
                  </div>
                  <div 
                    className="stat-item clickable-stat-item" 
                    onClick={() => {
                      setProductRequestForm(prev => ({
                        ...prev,
                        notes: t('sourceChina') + (prev.notes ? '\n' + prev.notes : '')
                      }));
                      setShowProductRequestModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fas fa-shield-alt"></i>
                    <div>
                      <strong>{t('safeImport')}</strong>
                      <span>{t('sourceChina')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
        {/* Features Section - Modern Cards */}
        <section className="features-section-modern">
          <Container>
            <Row className="g-4">
              <Col md={3} sm={6} xs={12}>
                <Card className="feature-card-modern">
                  <Card.Body className="text-center">
                    <div className="feature-icon-wrapper">
                      <i className="fas fa-shipping-fast"></i>
                    </div>
                    <h5 className="feature-title">{t('freeShipping')}</h5>
                    <p className="feature-description">{t('freeShippingDesc')}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} xs={12}>
                <Card className="feature-card-modern">
                  <Card.Body className="text-center">
                    <div className="feature-icon-wrapper">
                      <i className="fas fa-shield-alt"></i>
                    </div>
                    <h5 className="feature-title">{t('buyerProtection')}</h5>
                    <p className="feature-description">{t('qualityGuarantee')}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} xs={12}>
                <Card className="feature-card-modern">
                  <Card.Body className="text-center">
                    <div className="feature-icon-wrapper">
                      <i className="fas fa-headset"></i>
                    </div>
                    <h5 className="feature-title">{t('Support')}</h5>
                    <p className="feature-description">{t('customerService247')}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} xs={12}>
                <Card className="feature-card-modern">
                  <Card.Body className="text-center">
                    <div className="feature-icon mb-3 text-warning">
                      <i className="fas fa-undo-alt fa-2x"></i>
                    </div>
                    <h5 className="feature-title">{t('easyReturn')}</h5>
                    <p className="feature-description">{t('within30Days')}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Product Request Modal */}
        <Modal 
          show={showProductRequestModal} 
          onHide={() => {
            setShowProductRequestModal(false);
            setProductRequestError(null);
            setProductRequestSuccess(false);
            setProductRequestForm({
              productName: '',
              specifications: '',
              quantity: '',
              notes: '',
              requestType: '1'
            });
          }}
          size="lg"
          centered
          dir="rtl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-clipboard-list me-2"></i>
              طلب منتج جديد
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {productRequestSuccess ? (
              <Alert variant="success" className="text-center">
                <Alert.Heading>
                  <i className="fas fa-check-circle me-2"></i>
                  تم إرسال الطلب بنجاح!
                </Alert.Heading>
                <p>شكراً لك! سيتم مراجعة طلبك والرد عليك قريباً.</p>
              </Alert>
            ) : (
              <>
                {productRequestError && (
                  <Alert variant="danger" dismissible onClose={() => setProductRequestError(null)}>
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {productRequestError}
                  </Alert>
                )}

                <Form onSubmit={handleProductRequestSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      نوع الطلب <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="requestType"
                      value={productRequestForm.requestType}
                      onChange={handleProductRequestChange}
                      required
                      dir="rtl"
                    >
                      <option value="1">طلب توريد منتج</option>
                      <option value="2">طلب شحن</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      اسم المنتج <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="productName"
                      value={productRequestForm.productName}
                      onChange={handleProductRequestChange}
                      placeholder="أدخل اسم المنتج المطلوب"
                      required
                      dir="rtl"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>المواصفات</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="specifications"
                      value={productRequestForm.specifications}
                      onChange={handleProductRequestChange}
                      placeholder="أدخل مواصفات المنتج (الحجم، اللون، الموديل، إلخ...)"
                      dir="rtl"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      الكمية المطلوبة <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="quantity"
                      value={productRequestForm.quantity}
                      onChange={handleProductRequestChange}
                      placeholder="0"
                      min="0.01"
                      step="0.01"
                      required
                      dir="rtl"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>ملاحظات إضافية</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={productRequestForm.notes}
                      onChange={handleProductRequestChange}
                      placeholder="أي معلومات إضافية قد تساعدنا في تلبية طلبك..."
                      dir="rtl"
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="success"
                      disabled={productRequestLoading}
                      className="w-100"
                    >
                      {productRequestLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          جاري الإرسال...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          إرسال الطلب
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default Home;
